import React, { useState } from 'react';
import { MdHome, MdGrade, MdEventAvailable, MdPayments, MdPerson } from 'react-icons/md';
import { AuthProvider, useAuth } from './auth';
import { StudentDataProvider, useStudentData } from './StudentDataContext';
import { Spinner, ErrorState } from '../ui/Card';
import Login from './Login';
import Shell from './Shell';
import Home from './pages/Home';
import Grades from './pages/Grades';
import Attendance from './pages/Attendance';
import Fees from './pages/Fees';
import Profile from './pages/Profile';
import { IntroLayer, useIntroGate } from './intro/IntroGate';

const TABS = [
  { id: 'home', label: 'الرئيسية', icon: MdHome, Component: Home },
  { id: 'grades', label: 'العلامات', icon: MdGrade, Component: Grades },
  { id: 'attendance', label: 'الحضور', icon: MdEventAvailable, Component: Attendance },
  { id: 'fees', label: 'الأقساط', icon: MdPayments, Component: Fees },
  { id: 'profile', label: 'الملف', icon: MdPerson, Component: Profile },
];

const ACTIVE_TAB_KEY = 'mtzedu.student.tab';

/** The signed-in app: one data load, five tabs over it. */
function Portal({ onReplayIntro, onOpenTour }) {
  const { loading, error, reload } = useStudentData();
  const [active, setActive] = useState(() => {
    const saved = sessionStorage.getItem(ACTIVE_TAB_KEY);
    return TABS.some((t) => t.id === saved) ? saved : TABS[0].id;
  });

  const navigate = (id) => {
    setActive(id);
    sessionStorage.setItem(ACTIVE_TAB_KEY, id);
  };

  // The whole portal waits on one parallel load, so a page never renders
  // against half-arrived data and no tab has to show its own spinner.
  if (loading) return <div className="boot"><Spinner label="جارٍ تحميل بياناتك..." /></div>;
  if (error) return <div className="boot"><ErrorState message={error} onRetry={reload} /></div>;

  const current = TABS.find((t) => t.id === active) || TABS[0];
  const Page = current.Component;

  return (
    <Shell nav={TABS} active={current.id} onNavigate={navigate} onReplayIntro={onReplayIntro} onOpenTour={onOpenTour}>
      <Page />
    </Shell>
  );
}

function Root() {
  const { user, studentId } = useAuth();
  if (!user) return <Login />;
  // A signed-in account with no linked pupil row can load nothing — say
  // so plainly instead of firing five requests that all 404.
  if (!studentId) {
    return (
      <div className="boot">
        <ErrorState message="هذا الحساب غير مرتبط بطالب. يرجى مراجعة مكتب المدرسة." />
      </div>
    );
  }
  return <SignedIn user={user} />;
}

/** «بوابة مشكاة» welcome: after the first login, and once per new version. */
function SignedIn({ user }) {
  const intro = useIntroGate(user);
  return (
    <>
      <StudentDataProvider>
        <Portal onReplayIntro={intro.replay} onOpenTour={intro.openTour} />
      </StudentDataProvider>
      <IntroLayer gate={intro} user={user} />
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Root />
    </AuthProvider>
  );
}
